import { api } from "./api";
import {
  prepareImageUploadFile,
  type PreparedImageUpload,
} from "./image";

type ProfilePhotoWorkflowDependencies = {
  prepare: (file: File) => Promise<PreparedImageUpload>;
  upload: (prepared: PreparedImageUpload) => Promise<{ photo: { id: string } }>;
  remove: (photoId: string) => Promise<unknown>;
};

const defaultDependencies: ProfilePhotoWorkflowDependencies = {
  prepare: prepareImageUploadFile,
  upload: api.uploadPhoto,
  remove: api.deletePhoto,
};

/**
 * R2 and D1 cannot share one transaction. Upload new photos first and use
 * compensating deletes if any later step fails, so onboarding and edits remain
 * safely retryable without duplicate or half-finished photo submissions.
 */
export async function saveProfileWithPhotoRollback(
  files: readonly File[],
  saveProfile: () => Promise<unknown>,
  dependencies: ProfilePhotoWorkflowDependencies = defaultDependencies,
): Promise<void> {
  const uploadedPhotoIds: string[] = [];

  try {
    // Sequential preparation bounds mobile decode/encode memory even when a
    // member selects the full gallery at once.
    for (const file of files) {
      const prepared = await dependencies.prepare(file);
      const { photo } = await dependencies.upload(prepared);
      uploadedPhotoIds.push(photo.id);
    }
    await saveProfile();
  } catch (error) {
    await Promise.allSettled(
      uploadedPhotoIds.map((photoId) => dependencies.remove(photoId)),
    );
    throw error;
  }
}
